/* eslint-disable react/require-default-props */
/* eslint-disable camelcase */
import React, {
  createContext, useContext, useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { initIndexDB } from '@thd-olt-global/thd-storage-utils';
import { useMaaSession } from '../platform/session/session';
import { CHANNELS, createBroadcastChannel } from '../platform/events/broadcast';
import { emit, subscribe, unsubscribeAll } from '../platform/events/lifecycle';
import { createSlicedStore, combineSlices, useStore } from '../store/createStore';
import { composerSlice } from '../store/slices/composer';
import { fileUploadSlice } from '../store/slices/fileUpload';
import { suggestionsSlice } from '../store/slices/suggestions';
import { messagesSlice } from '../store/slices/messages';
import { useMessageHistory } from '../hooks/useMessageHistory';
import { LazyAssistantDataLayer } from './dynamic-import';
import { useLiveAgentBridge, liveAgentSlice } from '../liveAgent';
import { useDrawerControl } from '../shell/DrawerControlContext';
import { useFileUpload } from '../hooks/useFileUpload';
import {
  MESSAGE_ORIGIN_UNSPECIFIED,
  IMAGE_UPLOAD_ANALYTICS_EVENT_NAME,
  SUBMIT_ANALYTICS_MAA_PRIMARY_CATEGORY,
} from '../shared/constants';
import { updateConnectingToConnected as updateConnectingToConnectedUtil } from '../shared/utils/message-utils';
import { getDeviceType } from '../shared/utils/common';
import { handleBotResponse } from './flows/botResponse';
import { runAskUserQuestion } from './flows/askUserQuestion';

const indexDBConfig = {
  version: 2,
  stores: {
    sessions: {
      storeConfig: { keyPath: 'sessionId' },
      indexes: [{ name: 'sessionId', unique: true }],
    },
    messages: {
      storeConfig: { keyPath: 'id', autoIncrement: true },
      indexes: [{ name: 'id', unique: true }],
    }
  }
};

initIndexDB('assistantBotDB', indexDBConfig).catch(() => {});

const AssistantContext = createContext({
  store: null,
  historyCleared: false,
  historyLoading: false,
  messages: [],
  askUserQuestion: () => {},
  saveMessage: () => {},
  clearHistory: () => {},
  setHistoryCleared: () => {},
  resetConversation: () => {},
  updateMessage: () => {},
  // Sprinklr live-agent state
  isEscalated: false,
  isAgentConnected: false,
  // File upload state
  isSubmitting: false,
  setIsSubmitting: () => {},
  imageData: null,
  uploadError: null,
  setUploadError: () => {},
  isLoading: false,
  handleImageChange: () => {},
  handleSetFile: () => {},
  clearImage: () => {},
  documentFile: null,
  handleDocumentChange: () => {},
  clearDocument: () => {},
  isDocumentLoading: false,
});

// Injected as a prop, not read via useJourney() — this provider sits ABOVE JourneyProvider.
// Default is the isolated-mount fallback. See .claude/context/orchestrator-membership.md.
const DEFAULT_FLOWS = { runAskUserQuestion, handleBotResponse };

// Injected like DEFAULT_FLOWS and for the same reason; blocks are OPT-IN.
// See .claude/context/orchestrator-membership.md.
const DEFAULT_CONFIG = { liveAgent: true, queryLayer: true };

/**
 * @param { AssistantOrchestratorProps } props
 * @returns { React.Provider<AssistantContextProps> }
 */
export const AssistantOrchestrator = ({
  withPersistence = true, withDataLayer = true, flows = DEFAULT_FLOWS, config = DEFAULT_CONFIG,
  children,
}) => {
  // READ-ONLY — the Shell seeded this id; one page load must produce exactly one.
  // See .claude/context/session-identity.md.
  const [session] = useMaaSession();
  // Provider-level store (mirrors DrawerProvider); consumers read via selectors.
  // See .claude/context/orchestrator-membership.md.
  const storeRef = useRef(null);
  if (!storeRef.current) {
    storeRef.current = createSlicedStore(
      combineSlices(
        composerSlice, fileUploadSlice, suggestionsSlice, liveAgentSlice, messagesSlice
      )
    );
  }
  const store = storeRef.current;
  // A RATCHET, not a one-time freeze — grant-late must work. See
  // .claude/context/data-layer-sharing.md.
  const hasQueryLayerRef = useRef(config.queryLayer === true);
  if (config.queryLayer === true) hasQueryLayerRef.current = true;
  const hasQueryLayer = hasQueryLayerRef.current;
  // Reactive state lives in the store; the provider needs only the stable slice actions.
  // See .claude/context/orchestrator-membership.md.
  const { setHistoryCleared } = store.getState();
  // Internal — not part of the assistant's public surface.
  // See .claude/context/composition-topology.md ("Conversation reset").
  const maaBroadcastChannel = useRef(null);
  const { setIsSubmitting, setUploadError } = store.getState();

  const { hosts, channel, isConsumerApp } = useContext(ExperienceContext);
  // MAG4-856 — derived HERE, not in the block; the root owns the experience.
  // See .claude/context/orchestrator-membership.md.
  const deviceType = useMemo(
    () => getDeviceType({ channel, isConsumerApp }),
    [channel, isConsumerApp],
  );
  // Drawer control from App, passed into the flow ctx: askUserQuestion opens the drawer with a
  // direct downward call. Do not route it through a self-addressed cross-tab broadcast.
  const { openDrawer } = useDrawerControl();

  // Query layer — ⚠ the dispatch QUEUE is load-bearing, not defensive.
  // See .claude/context/orchestrator-membership.md ("The query layer is a BLOCK").
  const queryCapabilities = useRef({});
  const queuedCalls = useRef([]);
  const dispatchToQueryLayer = useCallback((name, arg) => {
    const capability = queryCapabilities.current[name];
    if (capability) return capability(arg);
    queuedCalls.current.push([name, arg]);
    return undefined;
  }, []);
  const registerQueryLayer = useCallback((capabilities) => {
    queryCapabilities.current = capabilities;
    const queued = queuedCalls.current;
    queuedCalls.current = [];
    queued.forEach(([name, arg]) => capabilities[name]?.(arg));
  }, []);
  const submitUserQuestionToApi = useCallback(
    (input) => dispatchToQueryLayer('submitUserQuestion', input), [dispatchToQueryLayer],
  );
  const submitGenerateQuestions = useCallback(
    (params) => dispatchToQueryLayer('submitGenerateQuestions', params), [dispatchToQueryLayer],
  );

  // No query layer, no conversation: nothing saved here could be sent, and `legacy`'s package
  // owns assistantBotDB. See .claude/context/orchestrator-membership.md.
  const {
    messages,
    lastBotMessage,
    lastUserMessage,
    saveMessage,
    saveMessageIfNew,
    clearHistory,
    updateMessage,
    broadCastMessageUpdate,
    iDBGetAll_messages,
    iDBUpdate_message,
    loadIDBMessages,
    commitSuggestedQuestions,
  } = useMessageHistory(withPersistence, session, store, submitGenerateQuestions, hasQueryLayer);

  // useMessageHistory publishes these to the store itself; do not add an effect-bridge. The
  // root reads them only for its own callbacks. See .claude/context/orchestrator-membership.md.

  const updateConnectingToConnected = useCallback(
    () => updateConnectingToConnectedUtil({
      getAll: iDBGetAll_messages,
      update: iDBUpdate_message,
      reload: loadIDBMessages,
    }),
    [iDBGetAll_messages, iDBUpdate_message, loadIDBMessages],
  );

  // LiveAgent — a BRIDGE, not the block; membership ratchets.
  // See .claude/context/live-agent.md and .claude/context/orchestrator-membership.md.
  const {
    checkIdleExpired,
    initiateAgentChat,
    sendAgentMessage,
    sendAgentImage,
    updateLastActivity,
    handleIdleDisconnect,
    openSurveyOverlay,
    closeSurveyOverlay,
    onSurveyDismiss,
  } = useLiveAgentBridge({
    // `=== true`, NOT `!== false` — blocks are opt-in, absence means OFF.
    // See .claude/context/orchestrator-membership.md.
    enabled: config.liveAgent === true,
    store,
    deviceType,
    conversation: {
      messages,
      saveMessage,
      saveMessageIfNew,
      updateMessage,
      broadCastMessageUpdate,
      updateConnectingToConnected,
    },
  });

  const {
    handleImageChange,
    handleSetFile,
    clearImage,
    handleDocumentChange,
    clearDocument,
  } = useFileUpload(setUploadError, store);

  // Owners publish into the store directly; do not add an effect-bridge.
  // See .claude/context/orchestrator-membership.md.

  // --- Hook Dependent State ---
  const sessionIDRef = useRef(null);
  const actionIDRef = useRef(null);
  const imageMetaRef = useRef(null);
  const messageHistoryRef = useRef(null);

  const sendImageUploadAnalytics = (sessionID, actionID, outcome, userQuestion) => {
    const imageMeta = imageMetaRef.current;
    if (!imageMeta) return;
    const hasText = userQuestion?.trim() ? 'yes' : 'no';
    const fileSizeKb = Number.isFinite(imageMeta.fileSize)
      ? Math.round(imageMeta.fileSize / 1024)
      : 0;
    const searchString = `maa:${imageMeta.imageOrigin} upload:${imageMeta.imageFileType}`
      + ` ${fileSizeKb}kb:text ${hasText}:${outcome}`;
    const analyticsObject = {
      primaryCategory: SUBMIT_ANALYTICS_MAA_PRIMARY_CATEGORY,
      eventName: IMAGE_UPLOAD_ANALYTICS_EVENT_NAME,
      maSessionID: sessionID,
      maActionID: actionID,
      maaSearch: searchString,
    };
    emit('magic-apron.image-submit', analyticsObject);
  };

  useEffect(() => {
    // Re-broadcast onto our own lifeCycle bus; adapter guards both ends.
    // See .claude/context/orchestrator-membership.md.
    const unsubscribePropagate = subscribe('propagate-maa-llm', ({ output }) => {
      emit('CONFIG_SERVICE_CHANGE', output, { channel: 'lifeCycle' });
    }, { frame: 'parent', channel: 'lifeCycle' });

    if (typeof BroadcastChannel !== 'undefined') {
      maaBroadcastChannel.current = createBroadcastChannel(CHANNELS.assistant);
    }
    return () => {
      unsubscribePropagate();
      if (maaBroadcastChannel.current) {
        maaBroadcastChannel.current.close();
      }
    };
  }, []);

  // --- Conversation reset --- two audiences: this tab, and peer tabs.
  // See .claude/context/composition-topology.md ("Conversation reset").
  const applyReset = useCallback(() => {
    clearHistory();
    setHistoryCleared(true);
  }, [clearHistory, setHistoryCleared]);

  // ⚠ Post from our OWN channel object — that is what prevents a double-reset.
  // See .claude/context/composition-topology.md ("Conversation reset").
  const resetConversation = useCallback(() => {
    applyReset();
    if (maaBroadcastChannel.current) {
      maaBroadcastChannel.current.postMessage({ type: 'MAA_RESET' });
    }
  }, [applyReset]);

  useEffect(() => {
    // Without the query layer, MAA_RESET is the legacy package's to apply, not ours.
    // See .claude/context/orchestrator-membership.md.
    if (maaBroadcastChannel.current && hasQueryLayer) {
      // Peer-originated resets only. MAA_RESET only — re-announcing readiness on
      // MAA_OPEN_DRAWER loops. See .claude/context/event-bus.md
      maaBroadcastChannel.current.onmessage = ({ data: { type } }) => {
        if (type === 'MAA_RESET') applyReset();
      };
    }
  }, [applyReset, maaBroadcastChannel, hasQueryLayer]);

  // Reconnect-on-reload belongs to the block's init(), not to a flow branch.
  // See .claude/context/live-agent.md.

  const askUserQuestion = useCallback((input) => flows.runAskUserQuestion(input, {
    session,
    hosts,
    imageData: store.getState().imageData,
    // Flows ask only about INTENT — isAgentConnected never crosses this boundary.
    // See .claude/context/orchestrator-membership.md.
    getIsEscalated: () => store.getState().isEscalated,
    sessionIDRef,
    actionIDRef,
    imageMetaRef,
    messageHistoryRef,
    openDrawer,
    messages,
    setIsSubmitting,
    setUploadError,
    setHistoryCleared,
    saveMessage,
    submitUserQuestionToApi,
    sendAgentMessage,
    sendAgentImage,
    initiateAgentChat,
    updateLastActivity,
    handleIdleDisconnect,
    checkIdleExpired,
    clearImage,
    broadCastMessageUpdate,
    sendImageUploadAnalytics,
  }), [
    session,
    clearHistory,
    submitUserQuestionToApi,
    saveMessage,
    broadCastMessageUpdate,
    checkIdleExpired,
    initiateAgentChat,
    sendAgentMessage,
    updateConnectingToConnected,
    flows.runAskUserQuestion
  ]);

  // Host page asks a question on our behalf (cross-frame). Handler via ref so the effect below
  // can be mount-only, the same way onBotTurnSettled reads its handlers.
  const askUserQuestionRef = useRef(askUserQuestion);
  askUserQuestionRef.current = askUserQuestion;

  useEffect(() => {
    // Without the query layer, never subscribe — and so never unsubscribeAll the legacy
    // package's own listener. See .claude/context/orchestrator-membership.md.
    if (!hasQueryLayer) return undefined;
    // ⚠ Subscribe once (hasQueryLayer only flips false → true), and tear down with
    // unsubscribeAll: the lifeCycle channel replays, and this handler renders.
    // See .claude/context/event-bus.md
    subscribe('magic-apron-assistant.user-ask-question', ({ output = {} }) => {
      const {
        question,
        messageOrigin = MESSAGE_ORIGIN_UNSPECIFIED,
        entryLocationForAnalytics,
        pageType,
        position
      } = output;
      if (question) {
        askUserQuestionRef.current({
          userQuestion: question,
          messageOrigin,
          suggestedQuestionPosition: position,
          entryLocationForAnalytics,
          pageType,
          isInitialSuggestedQuestion: true,
        });
      }
    }, { frame: 'parent', channel: 'lifeCycle' });

    return () => unsubscribeAll(
      'magic-apron-assistant.user-ask-question',
      { frame: 'parent', channel: 'lifeCycle' },
    );
  }, [hasQueryLayer]);

  // An event, not a watched flag; recreated every render on purpose (read via ref).
  // See .claude/context/orchestrator-membership.md ("onBotTurnSettled").
  const onBotTurnSettled = ({ data: botResponse, error: botError }) => {
    flows.handleBotResponse({
      botResponse,
      botError,
      messages,
      lastUserMessage,
      sessionIDRef,
      actionIDRef,
      saveMessage,
      setIsSubmitting,
      broadCastMessageUpdate,
      initiateAgentChat,
      updateConnectingToConnected,
      sendImageUploadAnalytics,
      clearActionId: () => { actionIDRef.current = null; },
    });
  };

  // Context exposes actions/refs/record/store only — reactive state lives in the store.
  // See .claude/context/orchestrator-membership.md.
  const context = {
    store,
    // Actions / handlers
    askUserQuestion,
    saveMessage,
    updateMessage,
    setIsSubmitting,
    setUploadError,
    handleImageChange,
    clearImage,
    handleDocumentChange,
    clearDocument,
    resetConversation,
    // Exposed so the Shell can retire the "New chat started" notice when the drawer session
    // ends. The flag is otherwise sticky: applyReset sets it and only the ask flow clears it.
    setHistoryCleared,
    onSurveyDismiss,
    openSurveyOverlay,
    closeSurveyOverlay,
    // Stable refs
    messageHistoryRef,
    // Session record (single source from useMaaSession)
    session,
  };

  // GraphQL boundary sits BELOW this root, dynamically imported — see
  // .claude/context/data-layer-sharing.md.
  const queryLayer = hasQueryLayer ? {
    session,
    store,
    register: registerQueryLayer,
    onSettled: onBotTurnSettled,
    onQuestionsSettled: commitSuggestedQuestions,
  } : null;

  // Every journey shares one data layer; `hasQueryLayer` gates only `queryLayer` above.
  // See .claude/context/data-layer-sharing.md.
  const wantsDataLayer = withDataLayer;

  return (
    <AssistantContext.Provider value={context}>
      {wantsDataLayer ? (
        <LazyAssistantDataLayer queryLayer={queryLayer}>
          {children}
        </LazyAssistantDataLayer>
      ) : children}
    </AssistantContext.Provider>
  );
};

AssistantOrchestrator.propTypes = {
  children: PropTypes.node,
  withPersistence: PropTypes.bool,
  // App's latch: false until the drawer has opened once, then true forever.
  withDataLayer: PropTypes.bool,
  // The active journey's flows, injected from above (see DEFAULT_FLOWS).
  flows: PropTypes.shape({
    runAskUserQuestion: PropTypes.func,
    handleBotResponse: PropTypes.func,
  }),
  // The active journey's block config, injected from above (see DEFAULT_CONFIG).
  config: PropTypes.shape({
    liveAgent: PropTypes.bool,
    queryLayer: PropTypes.bool,
  }),
};

/**
 * @returns { AssistantContextProps }
 */
export const useAssistant = () => {
  return useContext(AssistantContext);
};

// Reactive selector into the assistant store (mirrors useDrawerStore); pass `shallow`
// as isEqual to select several fields at once. See .claude/context/orchestrator-membership.md.
export const useAssistantStore = (selector, isEqual) => {
  const { store } = useContext(AssistantContext);
  return useStore(store, selector, isEqual);
};
